import React, { useState, useEffect, useMemo } from 'react';
import { IoClose } from "react-icons/io5";
import { FaBoxOpen, FaTruck, FaCheckCircle, FaExclamationCircle, FaFileInvoice, FaArrowRight, FaClock } from "react-icons/fa";
import { useSelector } from 'react-redux';
import displayINRCurrency from '../helpers/displayCurrency';
import SummaryApi from '../common';
import service from '../common/HttpService';
import moment from 'moment';
import useTranslate from '../hooks/useTranslate';
import Context from '../context';
import { useContext } from 'react';

const MyOrdersDrawer = ({ onClose, onOpenTrackOrder }) => {
    const context = useContext(Context)
    const { translateText, languageCode } = context
    const user = useSelector(state => state?.user?.user);
    const [orderData, setOrderData] = useState({ pending: [], process: [], completed: [], canceled: [] });
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('all'); // all, pending, process, complete, canceled
    const [translatedOrders, setTranslatedOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const t = useTranslate({
        myOrders: "My Orders",
        trackHistory: "Track & View Purchase History",
        all: "All",
        pending: "Pending",
        process: "Process",
        complete: "Complete",
        cancelled: "Cancelled",
        inProcess: "In Process",
        updatingList: "Updating List...",
        noHistory: "No history",
        orderId: "Order ID",
        qty: "Qty",
        payable: "Payable",
        assuredHistory: "FasalShop Assured History",
        secureTracking: "Secure Harvest Tracking & Digital Support",
        closeHistory: "Close History"
    });

    const fetchOrders = async () => {
        const userId = user?._id || user?.id;
        if (!userId) return;

        setLoading(true);
        try {
            const response = await service.post(SummaryApi.getCustomerOrders.url, {
                customer_id: String(userId),
                type: 1
            });

            if (response.success === "true" || response.success === true) {
                setOrderData({
                    pending: response.extraData?.pending || [],
                    process: response.extraData?.process || [],
                    completed: response.extraData?.completed || [],
                    canceled: response.extraData?.canceled || []
                });
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?._id || user?.id) {
            fetchOrders();
        }
    }, [user]);

    useEffect(() => {
        window.__activeModals = (window.__activeModals || 0) + 1;
        document.body.style.overflow = 'hidden';
        return () => {
            window.__activeModals = (window.__activeModals || 0) - 1;
            if (window.__activeModals <= 0) {
                document.body.style.overflow = 'unset';
                window.__activeModals = 0;
            }
        };
    }, []);

    const filteredOrders = useMemo(() => {
        let list = [];
        if (activeTab === 'all') {
            list = [...orderData.pending, ...orderData.process, ...orderData.completed, ...orderData.canceled];
        } else if (activeTab === 'pending') {
            list = orderData.pending;
        } else if (activeTab === 'process') {
            list = orderData.process;
        } else if (activeTab === 'complete') {
            list = orderData.completed;
        } else if (activeTab === 'canceled') {
            list = orderData.canceled;
        }

        return list.sort((a, b) =>
            moment(b.order_date, "DD-MM-YYYY HH:mm:ss").diff(moment(a.order_date, "DD-MM-YYYY HH:mm:ss"))
        );
    }, [orderData, activeTab]);

    useEffect(() => {
        const translateOrders = async () => {
            if (!filteredOrders.length || !languageCode || languageCode === 'en') {
                setTranslatedOrders(filteredOrders);
                return;
            }

            try {
                const translated = await Promise.all(filteredOrders.map(async (order) => {
                    const items = order.items || [];
                    const translatedItems = await Promise.all(items.map(async (item) => {
                        const rate = item.item_rate_id?.[0] || {};
                        const pName = item.itemname || rate.itemname || "Fasal Item";
                        return {
                            ...item,
                            translatedName: await translateText(pName, languageCode)
                        };
                    }));
                    return { ...order, items: translatedItems };
                }));
                setTranslatedOrders(translated);
            } catch (error) {
                console.error("Orders translation error:", error);
                setTranslatedOrders(filteredOrders);
            }
        };
        translateOrders();
    }, [filteredOrders, languageCode, translateText]);

    const getStatusUI = (order) => {
        if (orderData.completed.some(o => o.id === order.id)) {
            return { label: t.complete, color: 'text-green-600 bg-green-50 border-green-100', icon: <FaCheckCircle /> };
        }
        if (orderData.canceled.some(o => o.id === order.id)) {
            return { label: t.cancelled, color: 'text-red-500 bg-red-50 border-red-100', icon: <FaExclamationCircle /> };
        }
        if (orderData.process.some(o => o.id === order.id)) {
            return { label: t.inProcess, color: 'text-blue-500 bg-blue-50 border-blue-100', icon: <FaTruck /> };
        }
        return { label: t.pending, color: 'text-amber-500 bg-amber-50 border-amber-100', icon: <FaClock /> };
    };

    // Calculate total values from items
    const { totalMrp, totalSelling, totalQty } = useMemo(() => {
        if (!selectedOrder || !selectedOrder.items) return { totalMrp: 0, totalSelling: 0, totalQty: 0 };
        return selectedOrder.items.reduce((acc, item) => {
            const rate = item.item_rate_id?.[0] || {};
            const qty = Number(item.qty) || 0;
            const mrp = Number(rate.mrp) || Number(item.price) || Number(rate.salerate) || 0;
            const sale = Number(rate.salerate) || Number(item.price) || 0;

            return {
                totalMrp: acc.totalMrp + (mrp * qty),
                totalSelling: acc.totalSelling + (sale * qty),
                totalQty: acc.totalQty + qty
            };
        }, { totalMrp: 0, totalSelling: 0, totalQty: 0 });
    }, [selectedOrder]);

    const calculatedSavings = Math.max(0, totalMrp - totalSelling);
    const calculatedShipping = Math.max(0, Number(selectedOrder?.total_cost || 0) - totalSelling);

    return (
        <div className='fixed inset-0 z-[110] flex justify-end overflow-hidden'>
            <div className='absolute inset-0 bg-black/40 backdrop-blur-[2px] animate-in fade-in transition-opacity duration-300' onClick={onClose}></div>

            <div className='relative w-full md:w-[30%] bg-[#fdfdfd] h-full shadow-2xl flex flex-col transform transition-transform duration-500 animate-in slide-in-from-right-full'>

                {/* BRANDED HEADER */}
                <div className='shrink-0 bg-[#143d2c] pt-12 pb-10 px-6 relative overflow-hidden flex flex-col items-center justify-center text-center'>
                    <button
                        onClick={onClose}
                        className='absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-white/80 hover:text-white transition-all z-20'
                    >
                        <IoClose size={20} />
                    </button>

                    <div className='relative z-10 flex flex-col items-center'>
                        <div className='w-20 h-20 rounded-full border-2 border-[#f39200] flex items-center justify-center mb-6 shadow-2xl'>
                            <div className='w-full h-full rounded-full bg-black/10 flex items-center justify-center'>
                                <FaBoxOpen size={35} className='text-[#f39200]' />
                            </div>
                        </div>

                        <h2 className='text-3xl font-black text-white uppercase tracking-wider mb-2'>{t.myOrders}</h2>
                        <p className='text-[10px] text-[#f39200] font-black uppercase tracking-[2px]'>{t.trackHistory}</p>
                    </div>
                </div>

                {/* MODULAR TABS */}
                <div className='bg-white border-b border-slate-50 flex items-center px-4 py-3 gap-2 overflow-x-auto no-scrollbar shrink-0'>
                    {[
                        { id: 'all', label: t.all },
                        { id: 'pending', label: t.pending },
                        { id: 'process', label: t.process },
                        { id: 'complete', label: t.complete },
                        { id: 'canceled', label: t.cancelled }
                    ].map(tab => {
                        const count = tab.id === 'all'
                            ? (Object.values(orderData).reduce((a, b) => a + b.length, 0))
                            : (tab.id === 'complete' ? orderData.completed.length : orderData[tab.id]?.length || 0);

                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-2 rounded-xl text-[10px] md:text-[11px] font-black uppercase tracking-widest transition-all whitespace-nowrap min-w-[70px] ${activeTab === tab.id ? 'bg-[#143d2c] text-white shadow-md' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                            >
                                {tab.label} {count > 0 && `(${count})`}
                            </button>
                        );
                    })}
                </div>

                {/* SLIM ORDER CARDS */}
                <div className='flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-[#f8faf9]'>
                    {loading ? (
                        <div className="h-full flex flex-col items-center justify-center space-y-3 opacity-60">
                            <div className="w-8 h-8 border-[3px] border-[#00b259]/20 border-t-[#00b259] rounded-full animate-spin"></div>
                            <p className="text-[8px] font-black uppercase tracking-[2px]">{t.updatingList}</p>
                        </div>
                    ) : translatedOrders.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center opacity-30 py-20">
                            <FaBoxOpen size={40} className='text-slate-200 mb-4' />
                            <h3 className='text-xs font-black text-[#143d2c] uppercase tracking-wider'>{t.noHistory}</h3>
                        </div>
                    ) : (
                        translatedOrders.map((order, index) => {
                            const status = getStatusUI(order);
                            const items = order.items || [];

                            return (
                                <div key={order.id || index} onClick={() => setSelectedOrder(order)} className='bg-white rounded-[2rem] border border-slate-50 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group cursor-pointer hover:border-[#143d2c]/20'>
                                    {/* Small Header */}
                                    <div className='px-5 py-3.5 border-b border-slate-50 flex justify-between items-center bg-white'>
                                        <div>
                                            <p className='text-[9px] md:text-[10px] font-bold text-slate-400 uppercase tracking-tighter'>{t.orderId}</p>
                                            <h4 className='text-[12px] md:text-[13px] font-black text-slate-800 tracking-tight'>
                                                #{String(order.order_id).slice(-8).toUpperCase()}
                                            </h4>
                                        </div>
                                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] md:text-[10px] font-black border uppercase tracking-wider ${status.color}`}>
                                            {status.icon} {status.label}
                                        </div>
                                    </div>

                                    {/* Order Items */}
                                    <div className='px-5 py-4 space-y-3'>
                                        {items.map((item, i) => {
                                            const rate = item.item_rate_id?.[0] || {};
                                            const pName = item.itemname || rate.itemname || "Fasal Item";
                                            let pImg = item.image || rate.image || "";

                                            if (!pImg || pImg.includes("product-images/")) {
                                                pImg = "https://placehold.co/400x400/eaeff6/2a4b7c?text=FS";
                                            } else if (typeof pImg === 'string' && !pImg.startsWith('http')) {
                                                pImg = `https://fasalshop.com/${pImg.startsWith('/') ? pImg.substring(1) : pImg}`;
                                            }

                                            return (
                                                <div key={i} className='flex gap-3 group/item items-center'>
                                                    <div className='w-12 h-12 bg-slate-50 rounded-2xl p-1.5 border border-slate-50 shrink-0 flex items-center justify-center overflow-hidden'>
                                                        <img src={pImg} className='max-w-full max-h-full object-contain mix-blend-multiply group-hover/item:scale-110 transition-transform' alt="" />
                                                    </div>
                                                    <div className='flex-1 min-w-0'>
                                                        <p className='text-[10px] md:text-[12px] font-black text-slate-700 uppercase truncate mb-0.5'>{item.translatedName || pName}</p>
                                                        <div className='flex items-center justify-between'>
                                                            <div className='text-[9px] md:text-[11px] text-slate-400 font-bold'>{t.qty}: <span className='text-slate-800'>{item.qty}</span> <span className='mx-1 opacity-20'>|</span> {rate.unit_name || "UNIT"}</div>
                                                            <div className='text-[11px] md:text-[13px] font-black text-[#143d2c]'>{displayINRCurrency(rate.salerate || item.salerate || 0)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Small Summary Footer */}
                                    <div className='px-5 py-3.5 bg-slate-50/50 border-t border-slate-50 flex items-center justify-between'>
                                        <div className='flex flex-col'>
                                            <p className='text-[8px] md:text-[9px] font-black text-slate-400 uppercase leading-none mb-1'>{t.payable}</p>
                                            <p className='text-sm md:text-base font-black text-[#143d2c] leading-none'>{displayINRCurrency(order.total_cost)}</p>
                                        </div>

                                        <div className='flex items-center gap-2'>
                                            <div className='text-right'>
                                                <p className='text-[10px] md:text-[12px] font-bold text-slate-400 leading-none'>{moment(order.order_date, "DD-MM-YYYY").format('MMM DD')}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* PREMIUM FOOTER */}
                <div className="p-5 bg-white border-t border-slate-50 mt-auto shadow-[0_-10px_30px_rgba(0,0,0,0.03)] flex flex-col gap-4">
                    <div className='flex items-center gap-3 px-1'>
                        <div className='w-9 h-9 bg-slate-50 text-[#143d2c] rounded-xl flex items-center justify-center border border-slate-100'>
                            <FaFileInvoice size={15} />
                        </div>
                        <div className='flex flex-col'>
                            <p className='text-[9px] md:text-[11px] font-black text-[#143d2c] uppercase leading-none'>{t.assuredHistory}</p>
                            <p className='text-[8px] md:text-[10px] text-slate-400 font-bold mt-0.5'>{t.secureTracking}</p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="w-full bg-gradient-to-r from-[#143d2c] to-[#0d2a1f] hover:brightness-110 text-white font-black py-4 rounded-2xl flex items-center justify-center gap-2 transition-all uppercase tracking-[3px] text-[10px] md:text-[11px] shadow-xl shadow-green-900/10 active:scale-[0.98]"
                    >
                        {t.closeHistory}
                    </button>
                </div>
            </div>

            {/* ORDER DETAILS MODAL */}
            {selectedOrder && (
                <div className='fixed inset-0 z-[120] flex items-center justify-center p-4 animate-fade-in'>
                    <div className='absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300' onClick={() => setSelectedOrder(null)}></div>
                    <div className='relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh] border border-slate-100 z-10 transition-transform duration-300 scale-100'>

                        {/* Modal Header */}
                        <div className='bg-[#143d2c] text-white px-8 py-6 flex items-center justify-between shrink-0 relative overflow-hidden'>
                            <div className='absolute -top-10 -right-10 w-32 h-32 bg-[#00b259]/10 rounded-full blur-2xl'></div>
                            <div className='relative z-10'>
                                <p className='text-[10px] font-black text-[#f39200] uppercase tracking-[3px] mb-1'>Order Details</p>
                                <h3 className='text-base md:text-lg font-black tracking-tight'>#{String(selectedOrder.order_id).toUpperCase()}</h3>
                            </div>
                            <button
                                onClick={() => setSelectedOrder(null)}
                                className='bg-white/10 hover:bg-white/20 p-2.5 rounded-full text-white transition-all relative z-10'
                            >
                                <IoClose size={20} />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className='flex-1 overflow-y-auto p-6 md:p-8 space-y-6 custom-scrollbar bg-slate-50/30'>

                            {/* Status and Date */}
                            <div className='bg-white p-5 rounded-[2rem] border border-slate-100 flex items-center justify-between shadow-sm'>
                                <div>
                                    <p className='text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1'>Status</p>
                                    <div className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[10px] font-black border uppercase tracking-wider ${getStatusUI(selectedOrder).color}`}>
                                        {getStatusUI(selectedOrder).icon} {getStatusUI(selectedOrder).label}
                                    </div>
                                </div>
                                <div className='text-right'>
                                    <p className='text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1'>Order Date</p>
                                    <p className='text-xs md:text-sm font-black text-slate-700'>{moment(selectedOrder.order_date, "DD-MM-YYYY HH:mm:ss").format('MMM DD, YYYY hh:mm A')}</p>
                                </div>
                            </div>

                            {/* Items List */}
                            <div className='space-y-3.5'>
                                <h4 className='text-[10px] md:text-[11px] font-black text-slate-400 uppercase tracking-[3px] ml-2'>Items Ordered</h4>
                                <div className='space-y-3'>
                                    {(selectedOrder.items || []).map((item, i) => {
                                        const rate = item.item_rate_id?.[0] || {};
                                        const pName = item.itemname || rate.itemname || "Fasal Item";
                                        let pImg = item.image || (rate.image ? `https://admin1.fasalshop.com/product-images/${rate.image}` : "");

                                        if (!pImg || pImg === "https://www.fasalshop.com/product-images/" || pImg === "https://admin1.fasalshop.com/product-images/" || pImg.endsWith('/product-images/')) {
                                            pImg = "https://placehold.co/400x400/eaeff6/2a4b7c?text=FS";
                                        }

                                        return (
                                            <div key={i} className='bg-white p-4 rounded-[2rem] border border-slate-100 flex items-center gap-4 shadow-sm'>
                                                <div className='w-16 h-16 bg-slate-50 rounded-2xl p-2 border border-slate-50 shrink-0 flex items-center justify-center overflow-hidden'>
                                                    <img src={pImg} className='max-w-full max-h-full object-contain mix-blend-multiply' alt="" />
                                                </div>
                                                <div className='flex-1 min-w-0'>
                                                    <div className='flex items-center gap-2 mb-1 flex-wrap'>
                                                        <p className='text-xs font-black text-slate-800 uppercase truncate max-w-[200px]'>{item.translatedName || pName}</p>
                                                        {rate.category_name && (
                                                            <span className='text-[8px] bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-full border border-emerald-100 uppercase tracking-wider'>
                                                                {rate.category_name}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {rate.brand_name && (
                                                        <p className='text-[9px] text-slate-400 font-bold mb-1'>
                                                            Brand: <span className='text-slate-600'>{rate.brand_name}</span>
                                                        </p>
                                                    )}

                                                    <div className='flex items-center gap-1.5 mb-1'>
                                                        <span className='text-xs font-black text-[#143d2c]'>{displayINRCurrency(rate.salerate || item.salerate || 0)}</span>
                                                        {Number(rate.mrp) > Number(rate.salerate) && (
                                                            <>
                                                                <span className='text-[9px] text-slate-400 line-through'>{displayINRCurrency(rate.mrp)}</span>
                                                                <span className='text-[9px] text-emerald-600 font-bold'>
                                                                    (Save {displayINRCurrency(rate.mrp - rate.salerate)})
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>

                                                    <div className='flex items-center justify-between mt-2 pt-2 border-t border-slate-50'>
                                                        <p className='text-[10px] text-slate-400 font-bold'>
                                                            Qty: <span className='text-slate-800 font-black'>{item.qty}</span> <span className='mx-1 opacity-20'>|</span> {rate.unit_name || "UNIT"}
                                                        </p>
                                                        <p className='text-xs font-black text-[#143d2c]'>
                                                            {displayINRCurrency((rate.salerate || item.salerate || 0) * item.qty)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Shipping Address & Payment Method */}
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                <div className='bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col justify-between'>
                                    <div>
                                        <p className='text-[9px] font-black text-slate-400 uppercase tracking-wider mb-2'>Payment Method</p>
                                        <div className='flex items-center gap-2 text-slate-700'>
                                            <span className='font-black text-xs uppercase bg-slate-100 text-slate-600 px-2.5 py-1 rounded-lg border border-slate-200'>
                                                {selectedOrder.payment_method || 'COD'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className='mt-4 pt-4 border-t border-slate-100'>
                                        <p className='text-[9px] font-black text-slate-400 uppercase tracking-wider mb-1'>Delivery Partner</p>
                                        <p className='text-[10px] font-bold text-slate-600'>
                                            {selectedOrder.delivery_boy_id === "0" ? "Preparing / Pending Assignment" : `Assigned (ID: ${selectedOrder.delivery_boy_id})`}
                                        </p>
                                    </div>
                                </div>

                                <div className='bg-white p-5 rounded-[2rem] border border-slate-100 shadow-sm'>
                                    <p className='text-[9px] font-black text-slate-400 uppercase tracking-wider mb-2'>Shipping Address</p>
                                    {selectedOrder.address || selectedOrder.address_id ? (
                                        <div className='text-[10px] md:text-xs text-slate-600 font-bold space-y-1'>
                                            {typeof selectedOrder.address === 'object' ? (
                                                <>
                                                    <p className='font-black text-slate-800 text-[11px] md:text-[12px] uppercase'>{selectedOrder.address.name || selectedOrder.address.full_name || 'Recipient'}</p>
                                                    <p className='text-[9px] md:text-[10px] text-slate-400'>{selectedOrder.address.mobile || selectedOrder.address.phone}</p>
                                                    <p className='leading-relaxed text-slate-500 mt-1'>
                                                        {[
                                                            selectedOrder.address.house_no || selectedOrder.address.address_line1,
                                                            selectedOrder.address.landmark || selectedOrder.address.address_line2,
                                                            selectedOrder.address.city,
                                                            selectedOrder.address.state,
                                                            selectedOrder.address.pincode
                                                        ].filter(Boolean).join(', ')}
                                                    </p>
                                                </>
                                            ) : (
                                                <p className='leading-relaxed text-slate-500'>{selectedOrder.address}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='text-[10px] md:text-xs text-slate-500 font-bold space-y-1'>
                                            <p className='font-black text-slate-700 text-[11px] md:text-[12px] uppercase'>Home Delivery</p>
                                            <p className='leading-relaxed text-slate-400'>Delivered to registered address provided during checkout</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Price Breakdown */}
                            <div className='bg-[#143d2c]/5 p-6 rounded-[2rem] border border-[#143d2c]/10 space-y-2.5 shadow-inner'>
                                <div className='flex justify-between items-center text-[10px] md:text-xs font-bold text-slate-500'>
                                    <span>Items Subtotal</span>
                                    <span>{displayINRCurrency(totalSelling)}</span>
                                </div>
                                <div className='flex justify-between items-center text-[10px] md:text-xs font-bold text-slate-500'>
                                    <span>Shipping & Handling</span>
                                    <span>{calculatedShipping > 0 ? displayINRCurrency(calculatedShipping) : "FREE"}</span>
                                </div>
                                {calculatedSavings > 0 && (
                                    <div className='flex justify-between items-center text-[10px] md:text-xs font-bold text-green-600'>
                                        <span>Total Savings</span>
                                        <span>-{displayINRCurrency(calculatedSavings)}</span>
                                    </div>
                                )}
                                <div className='h-px bg-slate-200 my-2'></div>
                                <div className='flex justify-between items-center'>
                                    <span className='text-[10px] md:text-xs font-black text-slate-600 uppercase tracking-wider'>Total Cost</span>
                                    <span className='text-base md:text-lg font-black text-[#143d2c]'>{displayINRCurrency(selectedOrder.total_cost)}</span>
                                </div>
                            </div>

                        </div>

                        {/* Modal Footer */}
                        <div className='p-6 bg-white border-t border-slate-100 flex gap-4 shrink-0 shadow-[0_-10px_30px_rgba(0,0,0,0.02)]'>
                            <button
                                onClick={() => setSelectedOrder(null)}
                                className='flex-1 bg-[#143d2c] hover:bg-[#0d2a1f] text-white py-4 rounded-2xl font-black text-[10px] md:text-[11px] uppercase tracking-[2px] transition-all'
                            >
                                Close Details
                            </button>
                        </div>

                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(20,61,44,0.1); border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            ` }} />
        </div>
    );
};

export default MyOrdersDrawer;
